import { type JSX, useEffect, useState } from 'react';

import { ExportsApi } from 'Api/Exports/ExportsApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { DataGrid, GridColumn, dateColumn } from 'Components/DataGrid/DataGrid';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { EXPORTS } from 'Config/Paths';
import { getDepartmentFromSubject, getUserNameFromSubject } from 'Helpers/UserUtils';
import { AssessmentSubjectBase } from 'Models/AssessmentSubject';
import { AssessmentWorkflowState } from 'Models/DueDiligenceWorkflow';
import { DueDiligenceWorkflowExportRow } from 'Models/Exports';
import { UploadedFile } from 'Models/Files';
import { effectivenessAsString } from 'Models/OperationalControls';
import { ProductModule } from 'Models/ProductModule';
import { numberAsRiskRatingString } from 'Models/RiskRating';

interface TprmDueDiligenceWorkflowsExportProps {
    exportsApi: ExportsApi;
    module: ProductModule.TPRM;

    disableVirtualization?: boolean;
    ucrmApi?: never;
    ucrmAssessmentApi?: never;
}

interface UcrmDueDiligenceWorkflowsExportProps {
    module: ProductModule.UCRM;
    ucrmApi: ClientUcrmApi;
    ucrmAssessmentApi: UcrmAssessmentApi;

    disableVirtualization?: boolean;
    exportsApi?: never;
}

type DueDiligenceWorkflowsExportProps = TprmDueDiligenceWorkflowsExportProps | UcrmDueDiligenceWorkflowsExportProps;

/** Exports the due diligence workflows of TPRM third-party services or UCRM use cases, depending on `module`. */
export const DueDiligenceWorkflowsExport = (props: DueDiligenceWorkflowsExportProps): JSX.Element => {
    const [dueDiligenceWorkflowRows, setDueDiligenceWorkflowRows] = useState<DueDiligenceWorkflowExportRow[]>();
    const [error, setError] = useState<string>();
    const cachedData = useCachedData();

    useEffect(() => {
        const getExportRows = async (): Promise<void> => {
            try {
                if (props.module === ProductModule.UCRM) {
                    setDueDiligenceWorkflowRows(await getUcrmDueDiligenceWorkflowRows(props.ucrmApi, props.ucrmAssessmentApi));
                } else {
                    const response = await props.exportsApi.getThirdPartyServiceDueDiligenceWorkflowsExport();
                    setDueDiligenceWorkflowRows(response.data);
                }
            } catch (error) {
                setError(error.message);
            }
        };

        getExportRows();
    }, [props.exportsApi, props.module, props.ucrmApi, props.ucrmAssessmentApi]);

    const isUcrm = props.module === ProductModule.UCRM;
    const title = isUcrm ? 'Use Case Due Diligence Workflows' : 'Third-Party Service Due Diligence Workflows';

    const columns: GridColumn<DueDiligenceWorkflowExportRow>[] = [
        { field: 'third_party_name', headerName: isUcrm ? 'Business Name' : 'Third-Party Name', width: 300 },
        { field: 'service_name', headerName: isUcrm ? 'Use Case Name' : 'Service Name', width: 300 },
        { field: 'status', headerName: 'Status', width: 300 },
        { field: 'submitter_department', headerName: 'Submitter Department', width: 300, valueGetter: (_value, row) => (row.submitter === undefined ? undefined : getDepartmentFromSubject(row.submitter, cachedData.users)) },
        { field: 'submitter', headerName: 'Submitter', width: 300, valueGetter: (value) => (value === undefined ? undefined : getUserNameFromSubject(value, cachedData.users)) },
        dateColumn('completion_time', 'Completion Date'),
        { field: 'inherent_risk', headerName: 'Inherent Risk', width: 300, valueGetter: (value) => numberAsRiskRatingString(value) },
        { field: 'control_effectiveness', headerName: 'Control Effectiveness', width: 300, valueGetter: (value) => effectivenessAsString(value) },
        { field: 'residual_risk_score', headerName: 'Residual Risk', width: 300, valueGetter: (value) => numberAsRiskRatingString(value) },
        { field: 'assessment_documents', headerName: 'Final Review Documents', width: 300, valueGetter: (_value, row) => row.assessment_documents.map((file?: UploadedFile) => file?.filename).join(', ') },
        { field: 'additional_info', headerName: 'Final Review Additional Information', width: 300 },
        { field: 'common_assessment_parent', headerName: 'Using Shared Due Diligence Assessments Provided By', width: 300, valueGetter: (_value, row) => (row.common_assessment_parent ? row.common_assessment_parent.name : '') },
        dateColumn('assessment_due_date', 'Due Diligence Workflow Due Date'),
    ];

    if (dueDiligenceWorkflowRows) {
        return (
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={`/${EXPORTS}`}>Exports</BreadcrumbLink>
                        <BreadcrumbText>{title}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={`${title} Export`}
                body={[{ content: <DataGrid columns={columns} getRowId={(workflowRow: DueDiligenceWorkflowExportRow) => workflowRow.id} rows={dueDiligenceWorkflowRows} title={`${title} Export Preview`} fileName={isUcrm ? 'Use Cases Due Diligence Workflows' : 'Third-Party Services Due Diligence Workflows'} disableVirtualization={props.disableVirtualization} /> }]}
            />
        );
    } else if (error) return <Text color="darkBlue">{error}</Text>;
    else return <Placeholder />;
};

/**
 * Builds the UCRM due diligence workflows export from existing endpoints, rather than from a dedicated export endpoint. Mirrors how the back-end builds the TPRM export: one row per completed workflow (archive), plus one row per use case whose workflow is in progress.
 * Archives are requested per use case, and only for use cases that have been assessed at least once (i.e., that have a `last_assessed` time), since no other use case can have archives.
 */
const getUcrmDueDiligenceWorkflowRows = async (ucrmApi: ClientUcrmApi, ucrmAssessmentApi: UcrmAssessmentApi): Promise<DueDiligenceWorkflowExportRow[]> => {
    const useCases = (await ucrmApi.getUseCases()).data;
    const archivesResponses = await Promise.all(useCases.filter((useCase) => useCase.last_assessed !== undefined).map((useCase) => ucrmAssessmentApi.getAllDueDiligenceWorkflowArchivesForUseCase(useCase.vendor_id, useCase.id)));

    const completedWorkflowRows: DueDiligenceWorkflowExportRow[] = archivesResponses
        .flatMap((archivesResponse) => archivesResponse.data)
        .map((archive) => ({
            ...getDueDiligenceWorkflowRowMembers(archive.use_case),
            id: `${archive.use_case.id}#${archive.timestamp}`,
            status: 'Completed',
            submitter: archive.assessment_submitter_id,
            completion_time: archive.timestamp,
        }));

    const inProgressWorkflowRows: DueDiligenceWorkflowExportRow[] = useCases
        .filter((useCase) => useCase.assessment_workflow_data.state !== AssessmentWorkflowState.AWAITING_ASSESSMENT)
        .map((useCase) => ({
            ...getDueDiligenceWorkflowRowMembers(useCase),
            id: `${useCase.id}#None`,
            status: 'In Progress',
        }));

    return [...completedWorkflowRows, ...inProgressWorkflowRows];
};

/** Returns the members of a due diligence workflow export row that are taken directly from a use case, or from a use case's archive. */
const getDueDiligenceWorkflowRowMembers = (useCase: Pick<AssessmentSubjectBase, 'assessment_workflow_data' | 'assessment_workflow_setup' | 'name' | 'vendor_name'>): Omit<DueDiligenceWorkflowExportRow, 'completion_time' | 'id' | 'status' | 'submitter'> => {
    const finalReview = useCase.assessment_workflow_data.final_review_human_assessment;

    return {
        third_party_name: useCase.vendor_name,
        service_name: useCase.name,
        inherent_risk: useCase.assessment_workflow_data.irq_inherent_risk_score ?? 0,
        assessment_documents: finalReview.files,
        control_effectiveness: finalReview.effectiveness,
        residual_risk_score: finalReview.residual_risk,
        additional_info: finalReview.comments,
        common_assessment_parent: useCase.assessment_workflow_setup.common_assessment_parent,
        assessment_due_date: useCase.assessment_workflow_setup.due_date,
    };
};
